import { Component, HostBinding, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
	FieldExpression,
	LuceneExpression,
	NodeExpression,
	term,
	toString as luceneToString,
	parse as luceneToParse
} from 'lucene';
import { Observable, Subscription, debounceTime, distinctUntilChanged, map } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { ORDER_NUMBER_OPTIONS } from '../../../shared/models/list';
import { CUSTOMER } from '../../../shared/models/settings';
import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { OrderBy, OrderByDirection, UNDERSCORE } from '../../../shared/utils/constant';
import { DocumentService } from '../../services/documents.service';
import { Document, DocumentLine, FieldOfDocument, OrderNumbers } from '../../models/document';
import { ListFilter } from '../../../shared/models/filter';
import { DatacenterDocumentOrderNumberFilterType } from '../../../shared/models/datacenter-filters';
import { DatacenterType } from '../../../shared/models/datacenter-aggrid-view';
import { ListFilterService } from '../../../shared/services/list-filter.service';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';

import { OrderAcknowledgComponent } from './cards/order-acknowledgment.component';
import { DispatchAdviceComponent } from './cards/dispatch-advice.component';
import { CertificateComponent } from './cards/certificate.component';
import { InvoiceComponent } from './cards/invoice.component';

const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'date';

@Component({
	selector: 'copo-commercial-documents-list',
	templateUrl: './commercial-documents-list.component.html',
	styleUrl: './commercial-documents-list.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		VirtualScrollerDirective,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule,
		NgFor,
		InvoiceComponent,
		CertificateComponent,
		DispatchAdviceComponent,
		OrderAcknowledgComponent,
		LoadingSpinnerComponent
	]
})
export class CommercialDocumentsListComponent implements OnInit, OnDestroy {
	@HostBinding('class.copo-order-tracking-block') readonly block = true;

	readonly UNDERSCORE = UNDERSCORE;
	user: User;
	isLoading = false;
	searchTextControl = new FormControl('', { nonNullable: true });
	searchTextSubscription?: Subscription;
	filters = '';
	count = 0;
	orderDisplay: keyof typeof ORDER_NUMBER_OPTIONS = CUSTOMER;
	currentFilters?: ListFilter;
	datacenterType: DatacenterType;
	mergedFilter?: LuceneExpression;
	orderNumbers: OrderNumbers[] = [];
	requiredFields: readonly FieldOfDocument[] = [
		'customerOrderReference',
		'supplierOrderReference',
		'soldtoName',
		'shiptoName',
		'id',
		'type',
		'subtype',
		'date',
		'name',
		'paymentDueDate',
		'supplierVatNumber',
		'payerName',
		'payerVatNumber',
		'invoiceAmount',
		'invoiceAmountWithoutVat',
		'invoiceCurrency',
		'invoiceTaxAmount',
		'invoicedQuantity',
		'documentLines',
		'transportMode',
		'millCode'
	];

	constructor(
		private readonly documentService: DocumentService,
		private readonly userService: UserService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly listFilterService: ListFilterService
	) {
		this.datacenterType = <DatacenterType> this.route.snapshot.data['type'];
		this.currentFilters = this.getAppliedFilters();
		let criterionFilterNode;
		if (this.listFilterService
			.getLuceneFromAllFilters(this.currentFilters)) {
			criterionFilterNode = this.listFilterService.getLuceneFromAllFilters(this.currentFilters);
		}

		if (criterionFilterNode !== undefined) {
			this.mergedFilter = criterionFilterNode;
			this.filters = luceneToString(criterionFilterNode);
		}
		this.user = this.userService.user;
	}

	get dataId() {
		return `commercial-documents-${
			SORT_WAY
		}-${
			SORT_BY
		}-${
			this.filters
		}`;
	}

	ngOnInit() {
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.filters = value !== ''
				? this.mergedFilter
					? this.createQuickSearchAndFilterQuery(this.createQuickSearchFilter(value), this.mergedFilter)
					: this.createQuickSearchFilter(value)
				: this.mergedFilter ? luceneToString(this.mergedFilter) : '';
		});
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<Document[]>) => void) {
		provideData(
			this.documentService.getDocuments(
				this.requiredFields,
				this.filters,
				index,
				limit,
				new OrderBy(SORT_BY, SORT_WAY),
				false
			)
				.pipe(map(res => {
					this.count = res.count;
					return res.results;
				}))
		);
	}

	createQuickSearchFilter(textSearch: string) {
		const escapedTextFilter = `*${ term.escape(textSearch) }*`;

		const supplierOrderReferenceFieldFromDocument: FieldExpression = {
			field: 'supplierOrderReference',
			term: escapedTextFilter,
			quoted: false
		};

		const supplierOrderReferenceFieldFromDocumentLine: FieldExpression = {
			field: 'documentLines.supplierOrderReference',
			term: escapedTextFilter,
			quoted: false
		};

		const customerOrderReferenceFieldFromDocumentLine: FieldExpression = {
			field: 'documentLines.customerOrderReference',
			term: escapedTextFilter,
			quoted: false
		};

		const customerOrderReferenceFieldFromDocument: FieldExpression = {
			field: 'customerOrderReference',
			term: escapedTextFilter,
			quoted: false
		};

		const customerArticleReferenceField: FieldExpression = {
			field: 'documentLines.customerArticleReference',
			term: escapedTextFilter,
			quoted: false
		};

		const steelgradeField: FieldExpression = {
			field: 'documentLines.steelGrade',
			term: escapedTextFilter,
			quoted: false
		};

		const batchNumberField: FieldExpression = {
			field: 'documentLines.batchNumber',
			term: escapedTextFilter,
			quoted: false
		};

		const documentNameField: FieldExpression = {
			field: 'name',
			term: escapedTextFilter,
			quoted: false
		};

		const supplierNode = {
			left: supplierOrderReferenceFieldFromDocument,
			operator: 'OR',
			right: supplierOrderReferenceFieldFromDocumentLine,
			parenthesized: false
		};

		const customerNode = {
			left: customerOrderReferenceFieldFromDocument,
			operator: 'OR',
			right: customerOrderReferenceFieldFromDocumentLine,
			parenthesized: false
		};

		const batchDocumentNameField = {
			left: documentNameField,
			operator: 'OR',
			right: batchNumberField,
			parenthesized: false
		};

		const supplierCustomerNode = {
			left: supplierNode,
			operator: 'OR',
			right: customerNode,
			parenthesized: false
		};

		const articleSteelgradeNode = {
			left: customerArticleReferenceField,
			operator: 'OR',
			right: steelgradeField,
			parenthesized: false
		};

		const intermediateNode = {
			left: supplierCustomerNode,
			operator: 'OR',
			right: articleSteelgradeNode,
			parenthesized: false
		};

		const globalNode = {
			left: intermediateNode,
			operator: 'OR',
			right: batchDocumentNameField,
			parenthesized: false
		};

		return luceneToString(globalNode);
	}

	async goToFilter() {
		if (!this.currentFilters) {
			return;
		}
		this.listFilterService.filterToEdit = {
			filterState: this.currentFilters,
			datacenterType: this.currentFilters.datacenterType
		};
		await this.router.navigate(
			['filter'],
			{ queryParams: { type: this.datacenterType }, relativeTo: this.route }
		);
	}

	createQuickSearchAndFilterQuery(quicksearchQuery: string, filterQuery: LuceneExpression) {
		const globalNode: NodeExpression = {
			left: luceneToParse(quicksearchQuery),
			operator: 'AND',
			right: filterQuery,
			parenthesized: false
		};

		return luceneToString(globalNode);
	}

	getOrderName(document: Document, documentline: DocumentLine): string {
		if (document.customerOrderReference && documentline.customerOrderItemReference) {
			return `${ document.customerOrderReference }/${ documentline.customerOrderItemReference }`;
		} else if (document.customerOrderReference && !documentline.customerOrderItemReference) {
			return document.customerOrderReference;
		}
		return UNDERSCORE;
	}

	getUniqueOrderName(document: Document): OrderNumbers[] {
		const orderReference = new Set<string>();
		for (const docLine of document.documentLines) {
			if (docLine.customerOrderItemReference) {
				orderReference.add(`${ docLine.customerOrderReference }/${ docLine.customerOrderItemReference }`);
			} else {
				orderReference.add(docLine.customerOrderReference);
			}
		}
		for (const orderRef of orderReference) {
			const orderNumber = {
				key: orderRef,
				isSelected: false
			};
			this.orderNumbers.push(orderNumber);
		}
		return this.orderNumbers;
	}

	createFilter(filterObject: { document: Document; filterType: DatacenterType }) {
		const datacenterType = filterObject.filterType;
		switch (filterObject.filterType) {
		case 'order': {
			const id = 'supplierOrderNumber';
			const values = [];
			for (const docLine of filterObject.document.documentLines) {
				const value: DatacenterDocumentOrderNumberFilterType = {
					reference: filterObject.document.type === 'IN' || filterObject.document.type === 'DA'
						? docLine.supplierOrderReference : filterObject.document.supplierOrderReference,
					item: docLine.supplierOrderReferenceItem,
					entityReferenceName: 'document'
				};
				values.push(value);
			}
			const listFilter: ListFilter = {
				filterState: [
					{
						id,
						values
					}
				],
				datacenterType
			};
			this.listFilterService.setCurrentFilter(listFilter);
			break;
		}
		case 'batch': {
			const id = 'batchReference';
			const values: string[] = [];
			for (const docLine of filterObject.document.documentLines) {
				const value = docLine.batchNumber;
				values.push(value);
			}
			const listFilter: ListFilter = {
				filterState: [
					{
						id,
						values
					}
				],
				datacenterType
			};
			this.listFilterService.setCurrentFilter(listFilter);
			break;
		}
		default:
			console.error('undefined');
		}
	}

	private getAppliedFilters() {
		const listFilter: ListFilter = {
			filterState: [],
			datacenterType: this.datacenterType
		};
		const appliedFilter = this.listFilterService.getAppliedFilters(this.datacenterType)
			?? listFilter;
		this.listFilterService.setCurrentFilter(appliedFilter);
		return appliedFilter;
	}
}
